import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APP_CONSTANTS as CONST } from '@app/app.constants';
import { environment } from '@environment';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { APP_ROUTES as ROUTES } from '../../app.routes';
import { REQUEST_MOCK_TRANSACTION } from '../../data/mocks/app-control.mock';
import { ServerMock } from '../../data/mocks/app-serve.mock';
import { ErrorService } from '../../domain/services/error.service';
import { AppFacade } from '../../facades/app.facade';
import { UtilService } from '../../services/util.service';
import { ETRANSACTION_STATUS } from '../enums/app.enum';
import { ITransactions } from '../models/app.model';
import { HandleTokenService } from '../services/handle-token.service';

@Injectable()
export class AppTransactionInterceptor implements HttpInterceptor {
  private _localServer: ServerMock;

  constructor(
    private _handleTokenService: HandleTokenService,
    private _appFacade: AppFacade,
    private _utilService: UtilService,
    private _errorService: ErrorService
  ) {
    this._localServer = new ServerMock();
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return !this._checkIsTestEnv()
      ? this._handleRequest(request, next)
      : this._handleMockRequest(request, next);
  }

  private _checkIsTestEnv(): boolean {
    const { production, isTestServer } = environment;
    return !production && isTestServer;
  }

  private _handleRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const transactionToken = this._handleTokenService.getTransactionId();
    return !!transactionToken
      ? this._requestTransactionService(transactionToken, request, next)
      : this._cancelRequest(request, next);
  }

  private _handleMockRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const transactionToken = this._handleTokenService.getTransactionId();
    return !!transactionToken
      ? this._sendLocalServer(request, next)
      : this._cancelRequest(request, next);
  }

  private _requestTransactionService(
    transactionToken: string,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this._appFacade.getTransactions(transactionToken).pipe(
      mergeMap((transaction: ITransactions) => {
        const { status, redirect_url } = transaction;
        return status === ETRANSACTION_STATUS.PENDING
          ? this._continueRequest(req, next, transaction)
          : this._cancelRequest(req, next, redirect_url);
      }),
      catchError((error) => this._catchError(req, error))
    );
  }

  private _sendLocalServer(req: any, next: HttpHandler): Observable<HttpEvent<any>> {
    const httpRequest: any = REQUEST_MOCK_TRANSACTION;
    this._appFacade.getTransactionsMock();
    return this._localServer.request(httpRequest).pipe(
      mergeMap((transaction: any) => {
        const body: ITransactions = transaction?.body;
        const { status, redirect_url } = body;
        return status === ETRANSACTION_STATUS.PENDING
          ? this._continueRequest(req, next, body)
          : this._cancelRequest(req, next, redirect_url);
      })
    );
  }

  private _continueRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    transaction: ITransactions
  ): Observable<HttpEvent<any>> {
    this._appFacade.setTransactions(transaction);
    return next.handle(req);
  }

  private _cancelRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    redirectUrl?: string
  ): Observable<HttpEvent<any>> {
    const url = redirectUrl ?? ROUTES.ERROR;
    this._handleTokenService.removeAccessToken();
    this._handleTokenService.cancelTransactionId();
    this._handleTokenService.removeUserStep();
    this._utilService.redirectToUrl(url);
    this._appFacade.closeSession();
    return EMPTY;
  }

  private _catchError(req: HttpRequest<any>, err: HttpErrorResponse) {
    const { status } = err;
    const isUnathorized = status === CONST.HTTP_CODES.UNAUTHORIZED;

    if (isUnathorized) this._appFacade.closeSession();

    return throwError(err);
  }
}
